import {
  User,
  Briefcase,
  Calendar,
  Users,
  MessageSquare,
  Settings,
  Search,
  Heart,
  FileText,
  Building2,
  Compass,
  Award,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WhatsNextCard } from "./WhatsNextCard";
import { cn } from "@/lib/utils";
import type { User as UserType } from "@shared/schema";

interface NextStep {
  id: string;
  title: string;
  description: string;
  actionLabel: string;
  actionHref: string;
  icon: React.ReactNode;
  status: "pending" | "blocked" | "ready";
  blocker?: string;
  isComplete?: boolean;
  roleTag?: string;
}

interface RoleBasedNextStepsProps {
  user: UserType;
  className?: string;
  approvedRoles?: string[];
  singleRoleContext?: boolean;
}

function getFounderSteps(user: UserType): NextStep[] {
  const steps: NextStep[] = [];
  const hasProfile = user.founderCompanyName && user.founderStage;
  const hasOneLiner = !!user.founderOneLiner;

  if (!hasProfile) {
    steps.push({
      id: "founder-profile",
      title: "Complete Your Founder Profile",
      description: "Add your company info, stage, and sector to unlock platform features",
      actionLabel: "Complete Profile",
      actionHref: "/register/founder",
      icon: <User className="h-5 w-5" />,
      status: "ready",
      roleTag: "Founder",
    });
  }

  if (hasProfile && !hasOneLiner) {
    steps.push({
      id: "deal-readiness",
      title: "Check Your Deal Readiness",
      description: "Complete the deal readiness checklist to understand where you stand",
      actionLabel: "View Checklist",
      actionHref: "/capital/deal-readiness",
      icon: <Briefcase className="h-5 w-5" />,
      status: hasProfile ? "ready" : "blocked",
      blocker: hasProfile ? undefined : "Complete your profile first",
      roleTag: "Founder",
    });
  }

  if (hasProfile) {
    steps.push({
      id: "post-ask",
      title: "Post an Ask to the Community",
      description: "Get help from advisors and executives in our network",
      actionLabel: "Post Ask",
      actionHref: "/community/ask-board",
      icon: <MessageSquare className="h-5 w-5" />,
      status: "ready",
      roleTag: "Founder",
    });
  }

  if (hasProfile) {
    steps.push({
      id: "book-office-hours",
      title: "Book Office Hours",
      description: "Connect with advisors for personalized guidance",
      actionLabel: "Browse Sessions",
      actionHref: "/network/office-hours",
      icon: <Calendar className="h-5 w-5" />,
      status: "ready",
      roleTag: "Founder",
    });
  }

  return steps.slice(0, 3);
}

function getAdvisorSteps(user: UserType): NextStep[] {
  const steps: NextStep[] = [];
  const hasExpertise = user.expertise && user.expertise.length > 0;

  steps.push({
    id: "setup-office-hours",
    title: "Set Up Your Office Hours",
    description: "Configure your availability to help founders in our community",
    actionLabel: "Configure Hours",
    actionHref: "/network/office-hours",
    icon: <Calendar className="h-5 w-5" />,
    status: "ready",
    roleTag: "Advisor",
  });

  steps.push({
    id: "review-intros",
    title: "Review Pending Intro Requests",
    description: "Founders are waiting to connect with you",
    actionLabel: "View Requests",
    actionHref: "/network/intro-requests",
    icon: <Users className="h-5 w-5" />,
    status: "ready",
    roleTag: "Advisor",
  });

  if (!hasExpertise) {
    steps.push({
      id: "update-expertise",
      title: "Update Your Expertise",
      description: "Add your areas of expertise to get better-matched requests",
      actionLabel: "Edit Profile",
      actionHref: "/profile?edit=expertise",
      icon: <Settings className="h-5 w-5" />,
      status: "ready",
      roleTag: "Advisor",
    });
  }

  steps.push({
    id: "advisory-board",
    title: "Join an Advisory Board",
    description: "Offer your expertise to startups looking for advisors",
    actionLabel: "Browse Boards",
    actionHref: "/advisors",
    icon: <Building2 className="h-5 w-5" />,
    status: "ready",
    roleTag: "Advisor",
  });

  return steps.slice(0, 3);
}

function getInvestorSteps(user: UserType): NextStep[] {
  const steps: NextStep[] = [];
  const hasSectors = user.sectors && user.sectors.length > 0;
  const hasCheckSize = !!user.checkSize;

  if (!hasSectors || !hasCheckSize) {
    steps.push({
      id: "complete-investor-profile",
      title: "Complete Your Investor Profile",
      description: "Add your investment thesis and check size to access deal flow",
      actionLabel: "Complete Profile",
      actionHref: "/capital/investor-apply",
      icon: <User className="h-5 w-5" />,
      status: "ready",
      roleTag: "Investor",
    });
  }

  steps.push({
    id: "browse-dealroom",
    title: "Browse the Dealroom",
    description: "Discover vetted founders raising their next round",
    actionLabel: "View Dealroom",
    actionHref: "/capital/dealroom",
    icon: <Search className="h-5 w-5" />,
    status: hasSectors && hasCheckSize ? "ready" : "blocked",
    blocker: hasSectors && hasCheckSize ? undefined : "Complete your profile to access deal flow",
    roleTag: "Investor",
  });

  steps.push({
    id: "mark-interests",
    title: "Mark Your Interests",
    description: "Save companies you're interested in to your portfolio view",
    actionLabel: "View Portfolio",
    actionHref: "/capital/portfolio-hub",
    icon: <Heart className="h-5 w-5" />,
    status: hasSectors && hasCheckSize ? "ready" : "pending",
    roleTag: "Investor",
  });

  return steps.slice(0, 3);
}

function getExecutiveSteps(user: UserType): NextStep[] {
  const steps: NextStep[] = [];

  steps.push({
    id: "explore-content",
    title: "Explore Executive Content",
    description: "Read insights from top leaders in our network",
    actionLabel: "Browse Content",
    actionHref: "/media",
    icon: <FileText className="h-5 w-5" />,
    status: "ready",
    roleTag: "Executive",
  });

  steps.push({
    id: "connect-leaders",
    title: "Connect with Leaders",
    description: "Discover and connect with industry leaders in your sector",
    actionLabel: "View Leaders",
    actionHref: "/100-leaders",
    icon: <Award className="h-5 w-5" />,
    status: "ready",
    roleTag: "Executive",
  });

  steps.push({
    id: "board-opportunities",
    title: "Explore Board Opportunities",
    description: "Browse open board positions matching your expertise",
    actionLabel: "View Opportunities",
    actionHref: "/board-match",
    icon: <Briefcase className="h-5 w-5" />,
    status: "ready",
    roleTag: "Executive",
  });

  steps.push({
    id: "join-circles",
    title: "Join a Circle",
    description: "Participate in peer groups focused on your interests",
    actionLabel: "Browse Circles",
    actionHref: "/circles",
    icon: <Compass className="h-5 w-5" />,
    status: "ready",
    roleTag: "Executive",
  });

  return steps.slice(0, 3);
}

function getDefaultSteps(user: UserType): NextStep[] {
  const hasProfile = user.headline && user.bio;

  const steps: NextStep[] = [
    {
      id: "complete-general-profile",
      title: "Complete Your Profile",
      description: "Add your information to unlock full platform features",
      actionLabel: "Complete Profile",
      actionHref: "/profile?edit=missing",
      icon: <User className="h-5 w-5" />,
      status: hasProfile ? "pending" : "ready",
    },
    {
      id: "explore-content",
      title: "Explore Content",
      description: "Discover insights from top leaders in tech",
      actionLabel: "Browse Content",
      actionHref: "/media",
      icon: <FileText className="h-5 w-5" />,
      status: "ready",
    },
    {
      id: "join-community",
      title: "Join the Community",
      description: "Connect with other leaders and founders",
      actionLabel: "View Network",
      actionHref: "/network",
      icon: <Users className="h-5 w-5" />,
      status: "ready",
    },
  ];

  return steps;
}

function getAllSteps(user: UserType, approvedRoles: string[], singleRoleContext?: boolean): NextStep[] {
  const allSteps: NextStep[] = [];
  const seenIds = new Set<string>();

  if (!singleRoleContext) {
    const hasGeneralProfile = user.headline && user.bio;
    if (!hasGeneralProfile) {
      allSteps.push({
        id: "complete-general-profile",
        title: "Complete Your Profile",
        description: "Add your headline and bio to be discoverable by the community",
        actionLabel: "Complete Profile",
        actionHref: "/profile?edit=missing",
        icon: <User className="h-5 w-5" />,
        status: "ready",
        roleTag: "General",
      });
      seenIds.add("complete-general-profile");
    }
  }

  const roleFunctions: Record<string, (user: UserType) => NextStep[]> = {
    founder: getFounderSteps,
    investor: getInvestorSteps,
    advisor: getAdvisorSteps,
    executive: getExecutiveSteps,
  };

  for (const role of approvedRoles) {
    const fn = roleFunctions[role];
    if (fn) {
      const steps = fn(user);
      for (const step of steps) {
        if (!seenIds.has(step.id)) {
          seenIds.add(step.id);
          allSteps.push(step);
        }
      }
    }
  }

  if (approvedRoles.length === 0) {
    const defaults = getDefaultSteps(user);
    for (const step of defaults) {
      if (!seenIds.has(step.id)) {
        seenIds.add(step.id);
        allSteps.push(step);
      }
    }
  }

  return allSteps.slice(0, 5);
}

export function RoleBasedNextSteps({ user, className, approvedRoles, singleRoleContext }: RoleBasedNextStepsProps) {
  const roles = approvedRoles || (user.primaryRole ? [user.primaryRole] : []);
  const steps = getAllSteps(user, roles, singleRoleContext);

  if (steps.length === 0) {
    return null;
  }

  return (
    <Card className={cn("", className)} data-testid="card-role-based-next-steps">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2" data-testid="text-whats-next-heading">
          <Building2 className="h-5 w-5 text-primary" />
          What's Next for You
        </CardTitle>
        <p className="text-sm text-muted-foreground" data-testid="text-role-context">
          Personalized actions based on your roles
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {steps.map((step) => (
          <WhatsNextCard
            key={step.id}
            title={step.title}
            description={step.description}
            actionLabel={step.actionLabel}
            actionHref={step.actionHref}
            icon={step.icon}
            status={step.status}
            blocker={step.blocker}
            roleTag={step.roleTag}
          />
        ))}
      </CardContent>
    </Card>
  );
}
